import {
  Button,
  Container,
  Grid,
  Group,
  Select,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { SECTOR_OPTIONS } from '../../utils/constants/sectors';
import { useForm } from '@mantine/form';
import { IconArrowLeft, IconCheck, IconX } from '@tabler/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CustomLoader } from '../../components/custom-loader';
import { rootGoBack, rootNavigate } from '../../components/custom-router';
import { Page } from '../../components/page';
import { getCompaniesMinRequest } from '../../data/company';
import { updateEmployeeRequest } from '../../data/employees';
import { createOptionRequest, listOptionsRequest } from '../../data/options';
import { useGetEmployee } from '../../data/hooks/employee';
import {
  getUsersRequest,
  showUserRequest,
  updateUserRequest,
} from '../../data/user';
import { CompanyMin } from '../../models/company';
import { EmployeePeriod } from '../../models/employees';
import { User, UserRole, UserType } from '../../models/user';
import {
  errorNotification,
  successNotification,
} from '../../providers/mantine-notifications';
import { employeePeriodList } from '../../utils/constants/employee';
import { userRoleList } from '../../utils/constants/user';
import { AppRoutePaths } from '../../utils/enums/routes';
import { formatBRL, realToUSCash } from '../../utils/helpers';
import { formatCEP, formatCPF, formatRG } from '../../utils/masks';

export function UserEdit() {
  const { id } = useParams();
  const [pageLoading, setPageLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<User>();
  const [companyList, setCompanyList] = useState<CompanyMin[]>([]);
  const [userList, setUserList] = useState<User[]>([]);
  const [sectorData, setSectorData] = useState(SECTOR_OPTIONS);

  const companyOptions = companyList?.map((item) => ({
    label: item.fantasyName,
    value: String(item.id),
  }));

  const {
    fetch: getEmployeeFetcher,
    response: getEmployeeResponse,
    loading: getEmployeeLoader,
  } = useGetEmployee();

  const form = useForm({
    initialValues: {
      username: '',
      role: '',
      type: 'user',
      accessCode: '',
      password: '',
      manualLogin: false,
      isSectorSupervisor: false,
      birthDate: '',
      employee: {
        paymentPrice: '',
        transportPrice: '',
        needTransport: false,
        documentRG: '',
        documentCPF: '',
        documentPIS: '',
        period: '',
        addressZipCode: '',
        addressStreet: '',
        addressNumber: '',
        addressCity: '',
        note: '',
        company: '',
        supervisor: '',
        salary: '',
        email: '',
        sector: '',
      },
    },
  });

  async function getCompanies() {
    const response = await getCompaniesMinRequest();
    setCompanyList(response);
  }

  async function getUserInfo() {
    try {
      setPageLoading(true);
      const response = await showUserRequest(Number(id));
      await getEmployeeFetcher({
        id: Number(id),
      });
      setUserInfo(response);
      setPageLoading(false);
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao buscar usuário.',
        message: 'tente novamente.',
      });
    }
  }

  async function handleSubmit({ employee, ...values }: typeof form.values) {
    try {
      setPageLoading(true);
      await updateUserRequest(Number(id), {
        ...values,
        role: values.role as UserRole,
        type: values.type as UserType,
        password:
          form.values.password !== '' ? form.values.password : undefined,
      });

      await updateEmployeeRequest(Number(id), {
        ...employee,
        paymentPrice: employee.paymentPrice
          ? realToUSCash(employee.paymentPrice)
          : undefined,
        transportPrice: employee.transportPrice
          ? realToUSCash(employee.transportPrice)
          : undefined,
        period: employee.period as EmployeePeriod.DAY,
        company: employee.company ? Number(employee.company) : undefined,
        supervisor: Number(employee.supervisor),
        salary: employee.salary ? realToUSCash(employee.salary) : undefined,
        sector: employee.sector || undefined,
      });
      setPageLoading(false);
      successNotification({
        title: 'Usuário atualizado.',
        message: 'tudo certo.',
      });
      rootNavigate(AppRoutePaths.USERS);
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao atualizar usuário.',
        message: 'tente novamente.',
      });
    }
  }

  async function getUsers() {
    try {
      setPageLoading(true);
      const [supervisors, managers] = await Promise.all([
        getUsersRequest({ isSectorSupervisor: true }),
        getUsersRequest({ type: UserType.MANAGER }),
      ]);
      const merged = [
        ...supervisors,
        ...managers.filter((m) => !supervisors.some((s) => s.id === m.id)),
      ];
      setUserList(merged);
      setPageLoading(false);
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao buscar supervisores.',
        message: 'tente novamente',
      });
    }
  }

  useEffect(() => {
    getCompanies();
    getUserInfo();
    getUsers();
    listOptionsRequest('sector').then((options) => {
      setSectorData((prev) => {
        const existing = new Set(prev.map((o) => o.value));
        const extras = options
          .filter((o) => !existing.has(o.name))
          .map((o) => ({ value: o.name, label: o.name }));
        return extras.length ? [...prev, ...extras] : prev;
      });
    });
  }, []);

  useEffect(() => {
    form.setValues({
      accessCode: userInfo?.accessCode,
      role: userInfo?.role,
      username: userInfo?.username,
      manualLogin: userInfo?.manualLogin,
      isSectorSupervisor: userInfo?.isSectorSupervisor ?? false,
      birthDate: userInfo?.birthDate
        ? new Date(userInfo.birthDate).toISOString().split('T')[0]
        : '',
      employee: {
        paymentPrice: formatBRL(getEmployeeResponse?.paymentPrice) ?? undefined,
        transportPrice:
          formatBRL(getEmployeeResponse?.transportPrice) ?? undefined,
        addressCity: getEmployeeResponse?.addressCity ?? '',
        addressNumber: getEmployeeResponse?.addressNumber ?? '',
        addressStreet: getEmployeeResponse?.addressStreet ?? '',
        addressZipCode: getEmployeeResponse?.addressZipCode ?? '',
        documentCPF: formatCPF(getEmployeeResponse?.documentCPF ?? ''),
        documentPIS: getEmployeeResponse?.documentPIS ?? '',
        documentRG: formatRG(getEmployeeResponse?.documentRG ?? ''),
        needTransport: getEmployeeResponse?.needTransport ?? false,
        note: getEmployeeResponse?.note ?? '',
        period: getEmployeeResponse?.period ?? '',
        company: String(getEmployeeResponse?.company),
        supervisor: String(getEmployeeResponse?.supervisor),
        salary: formatBRL(getEmployeeResponse?.salary) ?? undefined,
        email: getEmployeeResponse?.email ?? '',
        sector: getEmployeeResponse?.sector ?? '',
      },
    });

    const currentSector = getEmployeeResponse?.sector;
    if (currentSector) {
      setSectorData((prev) =>
        prev.some((o) => o.value === currentSector)
          ? prev
          : [...prev, { value: currentSector, label: currentSector }],
      );
    }
  }, [getEmployeeResponse, userInfo]);

  return (
    <Page>
      <CustomLoader loading={pageLoading || getEmployeeLoader} />
      <Container bg="white" h="100%">
        <Button
          leftIcon={<IconArrowLeft />}
          onClick={rootGoBack}
          mt={16}
          mb={16}
          variant="subtle"
        >
          voltar
        </Button>
        <Title>Editar usuário</Title>
        <Text color="gray" mb={16}>
          dados do usuário
        </Text>
        <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
          <Grid columns={2} maw={700}>
            <Grid.Col span={1}>
              <TextInput
                required
                withAsterisk
                label="Nome"
                placeholder="nome completo"
                type="text"
                {...form.getInputProps('username')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                required
                withAsterisk
                label="Código de Acesso"
                placeholder="digite para alterar"
                type="text"
                {...form.getInputProps('accessCode')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Select
                required
                withAsterisk
                name="role"
                label="Tipo de usuário"
                placeholder="selecione o tipo e usuário"
                data={userRoleList}
                {...form.getInputProps('role')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Select
                required
                withAsterisk
                name="role"
                label="Periodo de trabalho"
                placeholder="selecione o periodo"
                data={employeePeriodList}
                {...form.getInputProps('employee.period')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Select
                searchable
                name="company"
                label="Empresa"
                placeholder="selecione a empresa"
                data={companyOptions ?? []}
                {...form.getInputProps('employee.company')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Select
                required
                withAsterisk
                searchable
                clearable
                label="Supervisor"
                placeholder="selecione o supervisor"
                data={userList?.map((item) => ({
                  label: item.username,
                  value: String(item.id),
                }))}
                mb={16}
                mr={8}
                {...form.getInputProps('employee.supervisor')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput
                label="Email"
                placeholder="email"
                type="text"
                {...form.getInputProps('employee.email')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="Data de Nascimento"
                placeholder="ex: 1990-12-31"
                type="date"
                {...form.getInputProps('birthDate')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                withAsterisk
                label="Senha"
                placeholder="digite uma nova senha"
                type="text"
                {...form.getInputProps('password')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Switch
                mt={8}
                mb={8}
                checked={form.values.manualLogin}
                color="teal"
                size="sm"
                label="Permitir acesso sem biometria?"
                thumbIcon={
                  form.values.manualLogin ? (
                    <IconCheck size="0.8rem" color="teal" stroke={3} />
                  ) : (
                    <IconX size="0.8rem" color="red" stroke={3} />
                  )
                }
                {...form.getInputProps('manualLogin')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Switch
                mt={8}
                mb={8}
                checked={form.values.isSectorSupervisor}
                color="blue"
                size="sm"
                label="Supervisor de setor?"
                thumbIcon={
                  form.values.isSectorSupervisor ? (
                    <IconCheck size="0.8rem" color="blue" stroke={3} />
                  ) : (
                    <IconX size="0.8rem" color="red" stroke={3} />
                  )
                }
                {...form.getInputProps('isSectorSupervisor')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Select
                label="Setor"
                placeholder="Selecione o setor"
                data={sectorData}
                searchable
                clearable
                creatable
                getCreateLabel={(q) => `+ Adicionar "${q}"`}
                onCreate={(q) => {
                  const item = { value: q, label: q };
                  setSectorData((prev) => [...prev, item]);
                  createOptionRequest({ name: q, type: 'sector' });
                  return item;
                }}
                {...form.getInputProps('employee.sector')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Text color="blue" fw="bold" size={14}>
                Custos
              </Text>
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="Valor da diária"
                placeholder="digite o valor"
                type="text"
                value={form.values.employee.paymentPrice}
                onChange={(e) =>
                  form.setFieldValue(
                    'employee.paymentPrice',
                    formatBRL(e.target.value),
                  )
                }
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Switch
                mt={32}
                checked={form.values.employee.needTransport}
                color="teal"
                size="sm"
                label="Recebe vale-transporte?"
                thumbIcon={
                  form.values.employee.needTransport ? (
                    <IconCheck size="0.8rem" color="teal" stroke={3} />
                  ) : (
                    <IconX size="0.8rem" color="red" stroke={3} />
                  )
                }
                {...form.getInputProps('employee.needTransport')}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                disabled={!form.values.employee.needTransport}
                label="Valor da condução"
                placeholder="digite o valor"
                type="text"
                value={form.values.employee.transportPrice}
                onChange={(e) =>
                  form.setFieldValue(
                    'employee.transportPrice',
                    formatBRL(e.target.value),
                  )
                }
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="Salário"
                placeholder="digite o valor"
                type="text"
                value={form.values.employee.salary}
                onChange={(e) =>
                  form.setFieldValue(
                    'employee.salary',
                    formatBRL(e.target.value),
                  )
                }
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Text color="blue" fw="bold" size={14}>
                Documentos
              </Text>
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="RG"
                placeholder="digite o documento"
                type="text"
                value={form.values.employee.documentRG}
                onChange={(e) =>
                  form.setFieldValue(
                    'employee.documentRG',
                    formatRG(e.target.value),
                  )
                }
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="CPF"
                placeholder="digite o documento"
                type="text"
                value={form.values.employee.documentCPF}
                onChange={(e) =>
                  form.setFieldValue(
                    'employee.documentCPF',
                    formatCPF(e.target.value),
                  )
                }
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="PIS"
                placeholder="digite o documento"
                type="text"
                {...form.getInputProps('employee.documentPIS')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Text color="blue" fw="bold" size={14}>
                Complementares
              </Text>
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="CEP"
                placeholder="ex: 01105-000"
                type="text"
                value={form.values.employee.addressZipCode}
                onChange={(e) =>
                  form.setFieldValue(
                    'employee.addressZipCode',
                    formatCEP(e.target.value),
                  )
                }
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <TextInput
                label="Número"
                placeholder="ex: 458"
                type="text"
                {...form.getInputProps('employee.addressNumber')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput
                label="Endereço"
                placeholder="ex: Rua João Teodoro"
                type="text"
                {...form.getInputProps('employee.addressStreet')}
              />
            </Grid.Col>

            <Grid.Col span={2}>
              <Text color="blue" fw="bold" size={14}>
                EPIs
              </Text>
            </Grid.Col>

            <Grid.Col span={2}>
              <Textarea
                label="Observações"
                placeholder="obs sobre o usuário"
                {...form.getInputProps('employee.note')}
              />
            </Grid.Col>
          </Grid>
          <Group mt={16} mb={16} position="right">
            <Button type="submit">Salvar</Button>
          </Group>
        </form>
      </Container>
    </Page>
  );
}
